/**
 * Ticking session countdown chip with an Extend action.
 * Renders nothing when the workspace has no expiry (not running).
 */

import { useEffect, useState, type ReactElement } from 'react';
import { useExtendWorkspace } from '../hooks/useWorkspaces';
import { EXPIRY_WARNING_MS, formatTimeRemaining } from '../utils/time';

interface SessionCountdownProps {
  workspaceId: string;
  expiresAt: string | null | undefined;
}

export function SessionCountdown({
  workspaceId,
  expiresAt,
}: SessionCountdownProps): ReactElement | null {
  const { mutateAsync: extendWorkspace, isPending } = useExtendWorkspace();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (expiresAt == null) return;
    const intervalId = setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { clearInterval(intervalId); };
  }, [expiresAt]);

  if (expiresAt == null) return null;

  const msRemaining = new Date(expiresAt).getTime() - now;
  const warning = msRemaining < EXPIRY_WARNING_MS;

  const handleExtend = async (): Promise<void> => {
    try {
      await extendWorkspace(workspaceId);
    } catch (err) {
      console.error('Failed to extend session:', err);
    }
  };

  return (
    <span className="session-countdown">
      <span
        className={`session-countdown__chip${warning ? ' session-countdown__chip--warning' : ''}`}
        aria-label="Session time remaining"
      >
        {formatTimeRemaining(msRemaining)}
      </span>
      <button
        type="button"
        onClick={() => { void handleExtend(); }}
        disabled={isPending}
        className="session-countdown__extend"
        aria-label="Extend session"
      >
        {isPending ? 'Extending...' : 'Extend'}
      </button>
    </span>
  );
}
